import React, { useState, useEffect } from 'react';
import { supabase } from '../utils/supabaseClient';
import { decrypt, setEncryptedCookie } from '../utils/cryptoUtils';
import './InitialScreen.css';
import logoImage from '../logo512.png';
import iconLogin from '../icon_login.png';
import iconInfo from '../icon_info.png';

const InitialScreen = ({ onAccessGranted }) => {
  const [inputValue, setInputValue] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isInfoTooltipVisible, setIsInfoTooltipVisible] = useState(false);
  const [decryptedPassword, setDecryptedPassword] = useState('');
  const [copied, setCopied] = useState(false);
  const [copyMessage, setCopyMessage] = useState('');
  const [showLoginTooltip, setShowLoginTooltip] = useState(false);
  const [showCopyTooltip, setShowCopyTooltip] = useState(false);
  const [processingUrl, setProcessingUrl] = useState(false);
  
  // URLから暗号化されたパスワードを取得して復号し、有効なら自動ログイン
  useEffect(() => {
    const autoLoginWithUrlPassword = async () => {
      setProcessingUrl(true);
      
      const searchParams = new URLSearchParams(window.location.search);
      const encryptedPw = searchParams.get('pw');
      
      console.log('URLパラメータ検出:', encryptedPw ? 'あり' : 'なし');
      
      if (encryptedPw) {
        try {
          console.log('復号処理開始:', encryptedPw);
          // cryptoUtils.jsの復号関数を使用
          const originalPassword = decrypt(encryptedPw);
          console.log('復号結果:', originalPassword ? '成功' : '失敗');
          
          if (originalPassword) {
            setDecryptedPassword(originalPassword);
            // 復号された合言葉を自動的に入力欄にセット
            setInputValue(originalPassword);
            console.log('復号された合言葉をセット:', originalPassword);
            
            // 合言葉が見つかったら自動的にSupabaseを検索
            if (originalPassword.length === 8) {
              try {
                console.log('自動ログイン処理開始');
                const { data, error } = await supabase
                  .from('hospitals')
                  .select('id, name, targetting')
                  .eq('word', originalPassword.toString())
                  .single();
                
                if (error || !data) {
                  console.error('自動ログイン失敗:', error);
                  // エラーがあっても画面は表示する（手動入力を促す）
                } else {
                  console.log('自動ログイン成功:', data.id, data.name, data.targetting);
                  // クッキーに病院IDと名前だけを保存（targettingは保存しない）
                  setEncryptedCookie('hospital_info', JSON.stringify({
                    id: data.id,
                    name: data.name
                  }));
                  // 自動ログイン成功、親コンポーネントに通知
                  onAccessGranted(data.id, data.name, data.targetting !== null ? data.targetting : true);
                }
              } catch (error) {
                console.error('自動ログイン処理エラー:', error);
              }
            }
          } else {
            console.error('復号に失敗しました。正しい暗号化パスワードではない可能性があります。');
          }
        } catch (error) {
          console.error('復号化エラー:', error);
        }
      }
      
      setProcessingUrl(false);
    };
    
    autoLoginWithUrlPassword();
  }, [onAccessGranted]);
  
  // テキストをクリップボードにコピー
  const copyToClipboard = () => {
    if (!decryptedPassword) return;
    
    navigator.clipboard.writeText(decryptedPassword)
      .then(() => {
        setCopied(true);
        setCopyMessage('合言葉をコピーしました');
        
        // 既存のタイマーをクリア
        if (window.copyMessageTimer) {
          clearTimeout(window.copyMessageTimer);
        }
        
        // 新しいタイマーを設定
        window.copyMessageTimer = setTimeout(() => {
          setCopyMessage('');
          setCopied(false);
        }, 3000); // 3秒後にメッセージを消す
      })
      .catch(err => {
        console.error('クリップボードへのコピーに失敗しました:', err);
      });
  };
  
  // ログインボタンの背景色
  const loginButtonBgColor = '#538B8C';

  const handleInputChange = (e) => {
    const value = e.target.value.toUpperCase();
    if (/^[A-Z0-9]{0,8}$/.test(value)) {
      setInputValue(value);
      setErrorMessage('');
    } else {
      setErrorMessage('入力内容を再度ご確認ください。');
    }
  };

  const handleSubmit = async () => {
    if (inputValue.length !== 8) {
      setErrorMessage('入力内容を再度ご確認ください。');
      return;
    }
    
    try {
      const { data, error } = await supabase
        .from('hospitals')
        .select('id, name, targetting')
        .eq('word', inputValue.toString())
        .single();
      
      if (error || !data) {
        console.error('認証エラー:', error);
        setErrorMessage('入力内容を再度ご確認ください。');
      } else {
        console.log('ログイン成功:', data.id, data.name, data.targetting);
        // クッキーに病院IDと名前だけを保存（targettingは保存しない）
        setEncryptedCookie('hospital_info', JSON.stringify({
          id: data.id,
          name: data.name
        }));
        onAccessGranted(data.id, data.name, data.targetting !== null ? data.targetting : true);
      }
    } catch (error) {
      console.error('ログイン処理エラー:', error);
      setErrorMessage('サーバーエラーが発生しました。再度お試しください。');
    }
  };

  const handleInfoMouseEnter = () => {
    setIsInfoTooltipVisible(true);
  };

  const handleInfoMouseLeave = () => {
    setIsInfoTooltipVisible(false);
  };

  const handleInfoClick = () => {
    setIsInfoTooltipVisible(!isInfoTooltipVisible);
  };

  return (
    <div className="initial-screen" style={{ 
      textAlign: 'center', 
      padding: '0 15px' // 左右に余白を追加
    }}>
      <div className="container mx-auto" style={{ paddingTop: '30px' }}>
        <div className="title-container text-center cursor-pointer flex items-center justify-center mb-6">
          <img 
            src={logoImage} 
            alt="Logo" 
            className="logo mr-2 cursor-pointer"
            style={{ 
              width: '50px', 
              height: 'auto',
              verticalAlign: 'middle'
            }}
            onClick={(e) => {
              e.stopPropagation();
              window.open('https://curelity.co.jp/', '_blank');
            }}
          />
          <h2 className="common-title mb-0 title-text" style={{ verticalAlign: 'middle', lineHeight: '1.2' }}>
            ほねなび
          </h2>
        </div>
      
        {/* 合言葉が見つかりましたメッセージ */}
        <div style={{ marginBottom: '10px', textAlign: 'center' }}>
          {decryptedPassword && decryptedPassword.length > 0 ? (
            <div style={{ 
              fontWeight: 'bold', 
              marginBottom: '10px', 
              fontSize: '16px', 
              color: '#2196F3',
              lineHeight: '1.4'
            }}>
              合言葉が見つかりました。<br />
              このまま<span style={{ color: loginButtonBgColor, fontWeight: 'bold' }}>ログインボタン</span>を押してください。
            </div>
          ) : (
            <p style={{ textAlign: 'center' }}>
              合言葉を入力してください。
            </p>
          )}
        </div>
        
        {errorMessage && <p style={{ color: 'red' }}>{errorMessage}</p>}
        <div style={{ 
          position: 'relative', 
          display: 'flex', 
          maxWidth: '500px', 
          width: '100%',
          margin: '0 auto 10px',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px'
        }}>
          <input
            type="tel"
            value={inputValue}
            onChange={handleInputChange}
            onKeyPress={(e) => {
              if (e.key === 'Enter') handleSubmit();
            }}
            placeholder="8桁の合言葉を入力"
            style={{ 
              borderColor: errorMessage ? 'rgba(255, 0, 0, 0.5)' : 'initial',
              backgroundColor: errorMessage ? 'rgba(255, 0, 0, 0.1)' : 'initial',
              border: '1px solid #ccc',
              padding: '8px 10px',
              height: '36px',
              borderRadius: '4px',
              flexGrow: '1',
              maxWidth: 'calc(100% - 60px)',
              fontSize: '16px',
              fontFamily: "Kosugi, sans-serif",
              boxSizing: 'border-box'
            }}
          />
          <div className="tooltip-container">
            <button 
              onClick={handleSubmit}
              className="login-button"
              onMouseEnter={() => setShowLoginTooltip(true)}
              onMouseLeave={() => setShowLoginTooltip(false)}
              title="ログイン"
              disabled={processingUrl}
            >
              <img 
                src={iconLogin} 
                alt="ログイン" 
                style={{ width: '24px', height: '24px' }} 
              />
            </button>
            {showLoginTooltip && (
              <div className="custom-tooltip" style={{
                top: '40px',
                left: '50%',
                transform: 'translateX(-50%)'
              }}>
                ログイン
              </div>
            )}
          </div>
        </div>
        
        {/* 情報アイコン - 入力欄の下に配置 */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', marginBottom: '20px' }}>
          <img 
            src={iconInfo}
            alt="情報" 
            style={{ 
              width: '24px', 
              height: '24px', 
              cursor: 'help'
            }} 
            onMouseEnter={handleInfoMouseEnter}
            onMouseLeave={handleInfoMouseLeave}
            onClick={handleInfoClick}
          />
          <div 
            className="tooltip" 
            style={{
              display: isInfoTooltipVisible ? 'block' : 'none',
              position: 'absolute',
              top: '25px',
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: 'white',
              border: '1px solid #ccc',
              padding: '10px',
              borderRadius: '4px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              fontFamily: "Kosugi, sans-serif",
              zIndex: 1000,
              width: '300px',
              textAlign: 'left',
              fontSize: '14px',
              lineHeight: '1.5'
            }}
          >
            <div>
              ※合言葉を入力して、ほねなびにアクセスします。医療機関専用のQRコードを読み取るとこの画面をスキップできます。<br />
              {decryptedPassword && decryptedPassword.length > 0 && (
                <>
                  ※下の合言葉を押すとコピーできます。
                </>
              )}
            </div>
          </div>
        </div>
        
        {/* 次に復号済合言葉のセクション */}
        {decryptedPassword && decryptedPassword.length > 0 && (
          <div style={{ 
            marginBottom: '20px', 
            width: '100%',
            maxWidth: '500px',
            margin: '0 auto 20px',
            boxSizing: 'border-box'
          }}>
            <p style={{ 
              fontWeight: 'bold', 
              fontSize: '16px', 
              color: '#000',
              lineHeight: '1.4',
              marginBottom: '10px'
            }}>
              QRコードから取得した合言葉
            </p>
            
            <div style={{ 
              position: 'relative', 
              display: 'flex',
              alignItems: 'center', 
              justifyContent: 'center',
              margin: '0 auto'
            }}>
              <div className="tooltip-container">
                <div
                  onClick={copyToClipboard}
                  className="password-display"
                  onMouseEnter={() => setShowCopyTooltip(true)}
                  onMouseLeave={() => setShowCopyTooltip(false)}
                  title="クリックしてコピー"
                >
                  {decryptedPassword}
                </div>
                {showCopyTooltip && (
                  <div className="custom-tooltip" style={{
                    top: '40px',
                    left: '50%',
                    transform: 'translateX(-50%)'
                  }}>
                    コピー
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        
        {/* コピー完了メッセージ */}
        {copyMessage && (
          <div className="copy-message">
            {copyMessage}
          </div>
        )}
      </div>
    </div>
  );
};

export default InitialScreen; 